import type { StaffRole } from './staff.schema';

/**
 * Who may do what (Sprint 2 KB, PERMS). One pure function so the API enforces it and the web hides what a
 * role cannot do. The API stays the authority; hiding in the UI is only a courtesy.
 */
export type Actor = { role: StaffRole | 'client' };

export type Action =
  | 'project.view'
  | 'project.create'
  | 'task.view'
  | 'task.create'
  | 'task.work'
  | 'task.publish'
  | 'task.hotfix.live'
  | 'task.hotfix.draft'
  | 'people.add'
  | 'people.add.unapproved'
  | 'vault.logins'
  | 'deliverable.manage'
  | 'deliverable.delete';

/** Facts about the thing being acted on that only the caller can look up (the schema package has no DB). */
export interface Resource {
  /** The actor is an account manager of the business this belongs to. */
  managesBusiness?: boolean;
  /** The actor is assigned to the task, or to a task in the project. */
  assigned?: boolean;
  /** The business is the client's own. */
  ownBusiness?: boolean;
}

export function can(actor: Actor, action: Action, resource: Resource = {}): boolean {
  switch (actor.role) {
    case 'admin':
      return action !== 'task.hotfix.draft' && action !== 'people.add.unapproved';
    case 'account_manager':
      switch (action) {
        case 'project.view':
        case 'project.create':
        case 'task.view':
        case 'task.create':
        case 'task.work':
        case 'task.publish':
        case 'task.hotfix.live':
        case 'people.add':
        case 'vault.logins':
        case 'deliverable.manage':
          return !!resource.managesBusiness;
        default:
          return false;
      }
    case 'lead_specialist':
      switch (action) {
        case 'project.view':
        case 'task.view':
        case 'task.work':
        case 'task.publish':
        case 'task.hotfix.live':
        case 'vault.logins':
        case 'deliverable.manage':
        case 'deliverable.delete':
          return !!resource.assigned;
        // A lead may propose people, but they stay pending until an admin approves.
        case 'people.add.unapproved':
          return !!resource.assigned;
        default:
          return false;
      }
    case 'specialist':
      switch (action) {
        case 'project.view':
        case 'task.view':
        case 'task.work':
        case 'task.hotfix.draft':
        case 'vault.logins':
        case 'deliverable.manage':
          return !!resource.assigned;
        default:
          return false;
      }
    case 'client':
      return (action === 'project.view' || action === 'task.view') && !!resource.ownBusiness;
  }
}
